import axios from 'axios';

import { classifyTransportError, getApiErrorMessage } from './errors';

/**
 * Whether a failed mutation gets the app-wide error toast.
 *
 * The toast is a SAFETY NET, not the primary way to report a failure. Most of
 * the 126 mutations in src/ pass no error handling at all, so today a failed
 * save is indistinguishable from a slow one — the button stops spinning and
 * nothing else happens. The net catches those.
 *
 * Opting out is DECLARED, never inferred, because react-query gives this
 * handler no way to infer it. A MutationCache handler is passed the Mutation,
 * so it can read hook options and meta — but every self-reporting call site in
 * src/ passes its handler at mutate() time (`save.mutate(vars, { onError })`),
 * and react-query keeps those in MutationObserver's `#mutateOptions`, an ES
 * private field on an object this handler never sees. The other reporting
 * shape, `{mutation.isError && <Alert/>}`, is JSX and equally invisible. So
 * both classes look exactly like an unhandled mutation from in here, and the
 * only honest signal is the one the call site writes down:
 *
 *   `meta: { suppressGlobalError: true }` — see REPORTS_OWN_ERRORS in
 *   callSiteErrors.ts, which is how hooks attach it.
 *
 * A hook-level `onError` is deliberately NOT read as an opt-out. It is not
 * evidence of anything: the one useMutation in src/ that passes onError in
 * hook options (instructor/hooks.ts) uses it to roll back an optimistic
 * update and tells the user nothing, so treating it as "handled" would silence
 * the exact failure this exists to surface.
 *
 * Nothing here invents new wording. A server that answered gets to keep its
 * own sentence — those are written for humans and are the only part of the
 * message with any detail in it — which is why the net can never SWALLOW a
 * specific error, only add one where there was silence.
 */

export const MUTATION_ERROR_FALLBACK = "That didn't go through. Please try again.";

export const MUTATION_TIMEOUT_MESSAGE =
  'That took too long to answer. Refresh before trying again — it may still have gone through.';

export const MUTATION_UNREACHABLE_MESSAGE =
  "We couldn't reach the Human Risk Academy. Check your connection and try again.";

/** The meta key callSiteErrors.REPORTS_OWN_ERRORS sets; matched exactly. */
export const SUPPRESS_GLOBAL_ERROR_META = 'suppressGlobalError';

/**
 * Structural stand-in for react-query's Mutation. Depending on the class would
 * force every test to build a real cache to assert a policy decision.
 */
export interface ReportableMutation {
  meta?: Record<string, unknown>;
  options?: {
    meta?: Record<string, unknown>;
  };
}

export function isGloballyReported(mutation?: ReportableMutation | null): boolean {
  const meta = mutation?.meta ?? mutation?.options?.meta;
  return meta?.[SUPPRESS_GLOBAL_ERROR_META] !== true;
}

export function mutationErrorMessage(error: unknown): string {
  // A throw that never became a request — a TypeError inside a mutationFn — is
  // a bug in this app, not a connectivity problem, and must not send anyone
  // off to check their wifi for it.
  if (!axios.isAxiosError(error)) return MUTATION_ERROR_FALLBACK;

  switch (classifyTransportError(error)) {
    case 'server':
      return getApiErrorMessage(error, MUTATION_ERROR_FALLBACK);
    case 'timeout':
      return MUTATION_TIMEOUT_MESSAGE;
    default:
      return MUTATION_UNREACHABLE_MESSAGE;
  }
}

/** The sentence to toast, or null when this mutation reports for itself. */
export function globalMutationErrorNotice(
  error: unknown,
  mutation?: ReportableMutation | null,
): string | null {
  return isGloballyReported(mutation) ? mutationErrorMessage(error) : null;
}
